// AnalyticsPage.tsx — Section VII: Site Analytics.
//
// "Visits, sources, and engagement on playdominus.com. Sourced from Cloudflare."
// (Lore-approved AN-03 revised: playdominus.com)
//
// Wire: Railway /admin/analytics endpoint (Cash B1.8, commit 99b40a5).
// No mock fallback per Phase B decision. Empty state when endpoint not configured.

import React from 'react';
import MetricCard from '../components/MetricCard';
import { useAnalytics } from '../hooks/useAnalytics';

export default function AnalyticsPage(): React.ReactElement {
  const { data, isLoading, error, refresh } = useAnalytics();

  return (
    <section className="section" id="analytics" aria-labelledby="analytics-title">
      <header className="section-header">
        <span className="section-eyebrow">Section VII</span>
        <h2 className="section-title" id="analytics-title">Site Analytics</h2>
        <p className="section-lede">
          Visits, sources, and engagement on playdominus.com. Sourced from Cloudflare.
        </p>
        {data && (
          <p className="section-meta">
            Last refreshed · {new Date(data.fetchedAt).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })} UTC
            {' '}
            <button className="btn-inline" onClick={refresh} disabled={isLoading}>
              {isLoading ? 'Refreshing' : 'Refresh'}
            </button>
          </p>
        )}
      </header>

      {error && (
        <div className="inline-error">{error}</div>
      )}

      {!data && !isLoading && !error ? (
        <div className="empty-state">
          <p className="empty-state-text">
            Awaiting first visit. Cloudflare populates fifteen minutes after launch.
          </p>
          <p className="empty-state-caption">
            Configure VITE_ADMIN_ANALYTICS_URL to wire the Cloudflare endpoint.
          </p>
        </div>
      ) : data ? (
        <>
          <div className="grid grid-4" style={{ marginBottom: 'var(--s-lg)' }}>
            <MetricCard
              label="Visits Today"
              numeral={data.visitsToday.toLocaleString('en-US')}
              caption="Pageviews since 00:00 UTC."
            />
            <MetricCard
              label="Visits / 7d"
              numeral={data.visits7d.toLocaleString('en-US')}
              caption="Past seven days."
            />
            <MetricCard
              label="Bounce Rate"
              numeral={`${data.bounceRate.toFixed(1)}`}
              numeralSub="%"
              caption="Single-page sessions."
            />
            <MetricCard
              label="Conversion / 7d"
              numeral={`${data.conversion7d.toFixed(2)}`}
              numeralSub="%"
              caption="Visit to subscription."
            />
          </div>

          <div className="grid grid-2">
            <div className="panel">
              <p className="panel-title">Top Pages · 7 days</p>
              <p className="panel-subtitle">Most-viewed paths on playdominus.com.</p>
              {data.topPages.length === 0 ? (
                <p className="card-caption">No page data yet.</p>
              ) : (
                <ul className="source-list">
                  {data.topPages.slice(0, 8).map((page) => {
                    const max = data.topPages[0]?.views ?? 1;
                    const pct = `${Math.round((page.views / max) * 100)}%`;
                    return (
                      <li key={page.path} className="source-row">
                        <span className="source-name">{page.path}</span>
                        <span className="source-value">{page.views.toLocaleString('en-US')}</span>
                        <div className="source-bar" style={{ '--w': pct } as React.CSSProperties} />
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            <div className="panel">
              <p className="panel-title">Traffic Sources · 7 days</p>
              <p className="panel-subtitle">How visitors arrive.</p>
              {data.trafficSources.length === 0 ? (
                <p className="card-caption">No source data yet.</p>
              ) : (
                <ul className="source-list">
                  {data.trafficSources.map((src) => (
                    <li key={src.name} className="source-row">
                      <span className="source-name">{src.name}</span>
                      <span className="source-value">{src.visitors.toLocaleString('en-US')}</span>
                      <div className="source-bar" style={{ '--w': `${src.share}%` } as React.CSSProperties} />
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </>
      ) : null}
    </section>
  );
}
